/**
 * How much of the pot went on the plate.
 *
 * One control for the two places a person eats from a pot that is not theirs
 * to weigh whole: the pot's own page, and the page a shared link opens. Both
 * used to offer whole portions and a hand-rolled "weigh it"; this is the one
 * row that answers "how much" for both, so the ¾ that works on one works on
 * the other.
 *
 * Six chips for the fractions people actually eat, "Other" for a number, and
 * "Weigh it" only when the pot was weighed — without a cooked weight a gram
 * figure maps to no portion (`helpingFor` in `@calorio/core`), and offering a
 * pad that would log a bowl at the raw density is the understatement weighing
 * exists to remove.
 *
 * The picker does not log. It says how much; the host renders the button that
 * says where — "Eat ¾ now · to lunch" on the pot, "Log 338 kcal to dinner" on
 * the shared page — because each host knows something about the destination
 * the other does not. The pad's own key is the exception: typing a number and
 * pressing "Log it" is one gesture, so the host may name that key and be told
 * when it fires.
 */

import {
  formatPortions,
  HELPING_FRACTIONS,
  parseDecimal,
  roundPortions,
  type Helping,
  type HelpingSource,
  type PortionChip,
} from "@calorio/core";
import { useState } from "react";

import { GramsPad } from "./GramsPad";

type Pad = "grams" | "portions" | null;

export function HelpingPicker({
  source,
  name,
  value,
  onChange,
  padAction = "Use it",
  onCommit,
  onPad,
}: {
  source: HelpingSource;
  /** What is being eaten, for the pad's header. */
  name: string;
  value: Helping;
  onChange: (helping: Helping) => void;
  /** The pad's key. "Use it" picks; a host that logs straight from the pad says so here. */
  padAction?: string;
  /** Fired when the pad's key is pressed, with what was typed, after `onChange`. */
  onCommit?: (helping: Helping) => void;
  /** Whether a pad is up, for a host that hides its own controls under one. */
  onPad?: (open: boolean) => void;
}) {
  const [pad, setPadState] = useState<Pad>(null);
  const [typed, setTyped] = useState("");
  const setPad = (next: Pad) => {
    setPadState(next);
    setTyped("");
    onPad?.(next !== null);
  };

  const perPortion = {
    kcal: source.kcal_per_portion ?? 0,
    protein_g: source.protein_g_per_portion ?? 0,
    carbs_g: source.carbs_g_per_portion ?? 0,
    fat_g: source.fat_g_per_portion ?? 0,
  };
  const weighable = source.weighed && !!source.portion_grams;

  const commit = () => {
    const number = parseDecimal(typed);
    if (!Number.isFinite(number) || number <= 0) return;
    const helping: Helping =
      pad === "grams"
        ? { kind: "grams", grams: number }
        : { kind: "portions", portions: roundPortions(number) };
    onChange(helping);
    setPad(null);
    onCommit?.(helping);
  };

  if (pad === "grams" && source.portion_grams) {
    const grams = source.portion_grams;
    // Per 100 g of the *cooked* pot, derived from the portion — the four
    // figures rather than kcal alone, so the pad's readout is right on all of
    // them and the diary row does not print P 0 · C 0 · F 0 after a weigh.
    const factor = 100 / grams;
    const chips: PortionChip[] = [0.5, 1, 1.5].map((count) => ({
      kind: "round",
      label: `${formatPortions(count)} portion${count > 1 ? "s" : ""}`,
      grams: Math.round(grams * count),
      detail: `${formatPortions(count)} of a ${Math.round(grams)} g portion`,
    }));
    return (
      <GramsPad
        name={name}
        value={typed}
        onChange={setTyped}
        unit="g"
        per100={{
          kcal: perPortion.kcal * factor,
          protein_g: perPortion.protein_g * factor,
          carbs_g: perPortion.carbs_g * factor,
          fat_g: perPortion.fat_g * factor,
        }}
        chips={chips}
        action={padAction}
        onCommit={commit}
      />
    );
  }

  if (pad === "portions") {
    return (
      <GramsPad
        name={name}
        value={typed}
        onChange={setTyped}
        unit="portions"
        // The pad prints the calories in "this much of this food" per 100 of
        // its unit, and here the unit is a portion: per 100 portions is the
        // per-portion figure times a hundred, which makes the readout right.
        per100={{
          kcal: perPortion.kcal * 100,
          protein_g: perPortion.protein_g * 100,
          carbs_g: perPortion.carbs_g * 100,
          fat_g: perPortion.fat_g * 100,
        }}
        chipsAdd={false}
        action={padAction}
        onCommit={commit}
      />
    );
  }

  const chosen = (count: number) =>
    value.kind === "portions" && roundPortions(value.portions) === count;

  return (
    <div className="helping" role="group" aria-label="How much">
      {HELPING_FRACTIONS.map((count) => (
        <button
          key={count}
          className={`pot__chip${chosen(count) ? " is-on" : ""}`}
          onClick={() => onChange({ kind: "portions", portions: count })}
        >
          {formatPortions(count)}
        </button>
      ))}
      <button
        className={`pot__chip${
          value.kind === "portions" && !HELPING_FRACTIONS.includes(roundPortions(value.portions))
            ? " is-on"
            : ""
        }`}
        onClick={() => setPad("portions")}
      >
        Other
      </button>
      {weighable && (
        <button
          className={`pot__chip${value.kind === "grams" ? " is-on" : ""}`}
          onClick={() => setPad("grams")}
        >
          {value.kind === "grams" ? `${Math.round(value.grams)} g` : "Weigh it"}
        </button>
      )}
    </div>
  );
}
